import React, { useState } from 'react';
import { X, Eye, EyeOff } from 'lucide-react';
import { Button } from '../ui/Button';
import { API } from '../../config';

/**
 * PasswordModal Component
 * Модальное окно для изменения пароля
 */
export const PasswordModal = ({ onClose, userEmail = null, token = null, onSuccess }) => {
  const [showPassword1, setShowPassword1] = useState(false);
  const [showPassword2, setShowPassword2] = useState(false);

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Валидация
    if (!newPassword || !confirmPassword) {
      setError('Пожалуйста, заполните все поля');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Пароли не совпадают');
      return;
    }

    if (newPassword.length < 8) {
      setError('Пароль должен содержать минимум 8 символов');
      return;
    }

    setIsLoading(true);

    try {
      // Вызываем API для смены пароля
      const response = await fetch(API.CHANGE_PASSWORD, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email: userEmail,
          new_password: newPassword,
          token: token
        })
      });

      const data = await response.json();

      if (response.ok && data.status === 'success') {
        console.log('✅ Password changed successfully');
        onClose();
        if (onSuccess) {
          onSuccess();
        }
      } else {
        setError(data.message || 'Ошибка при смене пароля');
      }
    } catch (error) {
      console.error('❌ Error changing password:', error);
      setError('Не удалось подключиться к серверу. Проверьте соединение.');
    } finally {
      setIsLoading(false);
    }
  };

  const generatePassword = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*';
    let password = '';
    for (let i = 0; i < 16; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setNewPassword(password);
    setConfirmPassword(password);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Overlay */}
      <div 
        className="absolute inset-0 bg-black/40"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-white rounded-xl shadow-2xl w-[446px] p-8">
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-7 right-8 text-gray-400 hover:text-gray-600 transition-colors"
        >
          <X size={16} />
        </button>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Заголовок */}
          <h3 className="text-[#2c2d2e] text-2xl font-medium">Изменение пароля</h3>

          {/* Описание */}
          <p className="text-[#2c2d2e] text-[15px] leading-5">
            После изменения пароля произойдёт выход из
            аккаунта на всех устройствах, сайтах и
            приложениях, где вошли с текущим паролем
          </p>

          {/* Отображение ошибок */}
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-lg text-[13px]">
              {error}
            </div>
          )}

          {/* Новый пароль */}
          <div className="space-y-1">
            <label className="text-[#2c2d2e] text-[13px] block">Новый пароль</label>
            <div className="relative">
              <input
                type={showPassword1 ? "text" : "password"}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="w-full border border-[#0077ff] rounded-lg px-3 py-2 text-[13px] pr-10 focus:outline-none focus:ring-2 focus:ring-[#0077ff]/20"
                placeholder="Введите новый пароль (минимум 8 символов)"
                disabled={isLoading}
              />
              <button
                type="button"
                onClick={() => setShowPassword1(!showPassword1)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showPassword1 ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
          </div>

          {/* Сгенерировать пароль */}
          <button
            type="button"
            onClick={generatePassword}
            className="text-[#0070f0] text-[13px] hover:text-[#0060d0] transition-colors"
            disabled={isLoading}
          >
            Сгенерировать пароль
          </button>

          {/* Новый пароль ещё раз */}
          <div className="space-y-1">
            <label className="text-[#2c2d2e] text-[13px] block">Новый пароль ещё раз</label>
            <div className="relative">
              <input
                type={showPassword2 ? "text" : "password"}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full border border-[#0077ff] rounded-lg px-3 py-2 text-[13px] pr-10 focus:outline-none focus:ring-2 focus:ring-[#0077ff]/20"
                placeholder="Повторите новый пароль"
                disabled={isLoading}
              />
              <button
                type="button"
                onClick={() => setShowPassword2(!showPassword2)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showPassword2 ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
          </div>

          {/* Кнопки */}
          <div className="flex gap-3 pt-4">
            <Button
              type="submit"
              variant="primary"
              className="flex-1"
              disabled={isLoading}
            >
              {isLoading ? 'Сохранение...' : 'Изменить пароль'}
            </Button>
            <Button
              type="button"
              variant="secondary"
              onClick={onClose}
              disabled={isLoading}
            >
              Отменить
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PasswordModal;